import { html } from '../utils/security.js';
import { getAppFactorySession, analyzeAppFactoryProgress } from '../api.js';

export const renderAppFactorySessionView = async (container, sessionId) => {
  container.innerHTML = html`
    <div class="container app-factory-session">
      <div class="page-header">
        <h1>App Factory Session</h1>
        <div class="session-actions">
          <button type="button" id="analyze-progress-btn" class="ghost-button">
            Fortschritt analysieren
          </button>
          <button type="button" id="finish-session-btn" class="primary-button">
            Session abschließen
          </button>
        </div>
      </div>

      <div class="app-factory-layout">
        <!-- Left: Guideline Panel -->
        <div class="guideline-panel" id="guideline-panel">
          <h2>Leitfaden</h2>
          <div id="guideline-content">
            <div class="loading-spinner">Lade Leitfaden...</div>
          </div>
          <div class="progress-summary" id="progress-summary"></div>
        </div>

        <!-- Right: Live Section (Placeholder für zukünftige Integration) -->
        <div class="live-panel">
          <h2>Konversation</h2>
          <div id="live-content">
            <p class="muted">
              In einer zukünftigen Version wird hier die Live-Transkription angezeigt.
            </p>
            <p class="muted small">Für jetzt können Sie bestehende Konversationen mit dieser Session verknüpfen.</p>
          </div>

          <div id="requirements-display" style="margin-top: 2rem;">
            <h3>Extrahierte Anforderungen</h3>
            <div id="requirements-list">
              <p class="muted">Noch keine Anforderungen extrahiert.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Load session data
  loadSessionData();

  // Event handlers
  document.getElementById('analyze-progress-btn')?.addEventListener('click', analyzeProgress);
  document.getElementById('finish-session-btn')?.addEventListener('click', finishSession);

  async function loadSessionData() {
    try {
      const response = await getAppFactorySession(sessionId);
      const session = response?.session;

      if (!session) {
        throw new Error('Session not found');
      }

      renderGuideline(session.progress);
      renderRequirements(response.extractedRequirements ?? []);
    } catch (error) {
      console.error('Failed to load session:', error);
      container.innerHTML = html`
        <div class="container">
          <div class="error-state">
            <h2>Fehler</h2>
            <p class="error-message">${error.message}</p>
            <button type="button" onclick="window.location.hash='#/app-factory'">
              Zurück zur Übersicht
            </button>
          </div>
        </div>
      `;
    }
  }

  function renderGuideline(progress) {
    const guidelineContent = document.getElementById('guideline-content');
    const progressSummary = document.getElementById('progress-summary');

    if (!progress || !progress.categories) {
      guidelineContent.innerHTML = '<p class="muted">Kein Leitfaden verfügbar.</p>';
      return;
    }

    guidelineContent.innerHTML = html`
      <div class="categories-list">
        ${progress.categories
          .map(
            (cat) => html`
              <div class="category-item ${cat.completed ? 'completed' : 'pending'}">
                <div class="category-header">
                  <span class="category-icon">${cat.completed ? '✅' : '⏳'}</span>
                  <h3>${cat.name}</h3>
                  ${cat.confidence ? html`<span class="confidence">${Math.round(cat.confidence * 100)}%</span>` : ''}
                </div>
                <div class="questions-list">
                  ${cat.questions
                    ?.map(
                      (q) => html`
                        <div class="question-item ${q.answered ? 'answered' : ''}">
                          <span class="question-icon">${q.answered ? '✓' : '○'}</span>
                          <span class="question-text">${q.text}</span>
                          ${
                            q.extractedInfo
                              ? html`<p class="extracted-info muted small">${q.extractedInfo}</p>`
                              : ''
                          }
                        </div>
                      `,
                    )
                    .join('') ?? ''}
                </div>
              </div>
            `,
          )
          .join('')}
      </div>
    `;

    progressSummary.innerHTML = html`
      <div class="progress-bar">
        <div class="progress-fill" style="width: ${progress.percentComplete ?? 0}%"></div>
      </div>
      <p class="muted small">
        ${progress.completedCategories ?? 0} / ${progress.totalCategories ?? 0} Kategorien abgeschlossen
        (${progress.percentComplete ?? 0}%)
      </p>
    `;
  }

  function renderRequirements(requirements) {
    const requirementsList = document.getElementById('requirements-list');

    if (!requirements || requirements.length === 0) {
      requirementsList.innerHTML = '<p class="muted">Noch keine Anforderungen extrahiert.</p>';
      return;
    }

    requirementsList.innerHTML = html`
      <div class="requirements-grid">
        ${requirements
          .map(
            (req) => html`
              <div class="requirement-card">
                <span class="requirement-type">${req.type ?? 'Anforderung'}</span>
                <p>${req.text ?? req.description}</p>
                ${req.priority ? html`<span class="priority priority-${req.priority}">${req.priority}</span>` : ''}
              </div>
            `,
          )
          .join('')}
      </div>
    `;
  }

  async function analyzeProgress() {
    const btn = document.getElementById('analyze-progress-btn');
    btn.disabled = true;
    btn.textContent = 'Analysiere...';

    try {
      const response = await analyzeAppFactoryProgress(sessionId);
      renderGuideline(response.progress);

      // Show success message
      alert('Fortschritt erfolgreich analysiert!');
    } catch (error) {
      console.error('Failed to analyze progress:', error);
      alert(`Fehler bei der Analyse: ${error.message}`);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Fortschritt analysieren';
    }
  }

  function finishSession() {
    window.location.hash = `#/app-factory/review/${sessionId}`;
  }
};
